import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectAddresses } from '../selectors';
import { addAddress, removeAddress, setDefaultAddress, updateAddress } from '../userSlice';
import { PlacesAutocompleteInput } from '@/features/checkout/PlacesAutocompleteInput';
import type { UserAddress } from '@/types';

interface Draft {
  label: string;
  instructions: string;
  // Geocoded fields populated by PlacesAutocompleteInput.onPick. Until a pick
  // happens these are undefined and the Save button stays disabled.
  place_id?: string;
  lat?: number;
  lng?: number;
  formatted_address?: string;
  street?: string;
  city?: string;
  zip?: string;
}

const emptyDraft: Draft = { label: '', instructions: '' };

function draftFromAddress(a: UserAddress): Draft {
  return {
    label: a.label,
    instructions: a.instructions ?? '',
    place_id: a.place_id,
    lat: a.lat,
    lng: a.lng,
    formatted_address: a.formatted_address,
    street: a.street,
    city: a.city,
    zip: a.zip,
  };
}

function placesInitialValue(d: Draft): string {
  if (d.formatted_address) return d.formatted_address;
  const parts = [d.street, d.zip && d.city ? `${d.zip} ${d.city}` : d.city || d.zip]
    .filter(Boolean)
    .join(', ');
  return parts;
}

export default function AddressesPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const addresses = useAppSelector(selectAddresses);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);

  const startEdit = (a: UserAddress) => {
    setEditingId(a.id);
    setAdding(false);
    setDraft(draftFromAddress(a));
  };

  const startAdd = () => {
    setAdding(true);
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const saveEdit = () => {
    if (!editingId || !draft.place_id) return;
    dispatch(
      updateAddress({
        id: editingId,
        patch: {
          label: draft.label,
          instructions: draft.instructions || undefined,
          street: draft.street ?? '',
          city: draft.city ?? '',
          zip: draft.zip ?? '',
          place_id: draft.place_id,
          lat: draft.lat,
          lng: draft.lng,
          formatted_address: draft.formatted_address,
        },
      }),
    );
    setEditingId(null);
  };

  const saveAdd = () => {
    if (!draft.place_id) return;
    dispatch(
      addAddress({
        id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `addr-${Date.now()}`,
        label: draft.label,
        street: draft.street ?? '',
        city: draft.city ?? '',
        zip: draft.zip ?? '',
        instructions: draft.instructions || undefined,
        isDefault: addresses.length === 0,
        place_id: draft.place_id,
        lat: draft.lat,
        lng: draft.lng,
        formatted_address: draft.formatted_address,
      }),
    );
    setAdding(false);
  };

  return (
    <div className="space-y-3">
      {addresses.map((a) => (
        <div key={a.id} className="bg-paper border-2 border-ink p-4 rounded-btn">
          {editingId === a.id ? (
            <DraftForm
              draft={draft}
              setDraft={setDraft}
              onSave={saveEdit}
              onCancel={() => setEditingId(null)}
            />
          ) : confirmDelId === a.id ? (
            <div className="p-3 border-2 border-ink bg-yellow-100 rounded-btn-sm flex gap-2 items-center">
              <span className="flex-1 text-sm">{t('account.addresses.deleteConfirm')}</span>
              <button
                type="button"
                onClick={() => {
                  dispatch(removeAddress({ id: a.id }));
                  setConfirmDelId(null);
                }}
                className="px-3 py-1 bg-ink text-paper font-display rounded-btn-sm text-sm"
              >
                {t('account.addresses.confirmDelete')}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelId(null)}
                className="px-3 py-1 bg-paper border-2 border-ink font-display rounded-btn-sm text-sm"
              >
                {t('account.addresses.editor.cancel')}
              </button>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-ink">
                  {a.label}{' '}
                  {a.isDefault && (
                    <span className="ms-2 text-xs px-2 py-0.5 bg-mint border border-ink rounded-btn-sm">
                      {t('account.addresses.default')}
                    </span>
                  )}
                  {!a.place_id && (
                    <span className="ms-2 text-xs px-2 py-0.5 bg-yellow-200 border border-ink rounded-btn-sm">
                      {t('account.addresses.editor.needsGeocode')}
                    </span>
                  )}
                </p>
                <p className="text-sm text-ink/80">{a.street}</p>
                <p className="text-sm text-ink/80">
                  {a.zip} {a.city}
                </p>
                {a.instructions && (
                  <p className="text-xs text-ink/60 mt-1">{a.instructions}</p>
                )}
                {!a.place_id && (
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    className="mt-2 px-2 py-1 border-2 border-ink rounded-btn-sm bg-yellow-50 font-display text-sm"
                  >
                    {t('account.addresses.editor.geocode')}
                  </button>
                )}
              </div>
              {confirmDelId === null && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    aria-label={t('account.addresses.edit')}
                    onClick={() => startEdit(a)}
                    className="px-2 py-1 border-2 border-ink rounded-btn-sm bg-paper text-sm"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    aria-label={t('account.addresses.setDefault')}
                    onClick={() => dispatch(setDefaultAddress({ id: a.id }))}
                    disabled={a.isDefault}
                    className="px-2 py-1 border-2 border-ink rounded-btn-sm bg-paper text-sm disabled:opacity-30"
                  >
                    ⭐
                  </button>
                  <button
                    type="button"
                    aria-label={t('account.addresses.delete')}
                    onClick={() => setConfirmDelId(a.id)}
                    disabled={a.isDefault}
                    title={
                      a.isDefault
                        ? t('account.addresses.deleteDefault')
                        : undefined
                    }
                    className="px-2 py-1 border-2 border-ink rounded-btn-sm bg-paper text-sm disabled:opacity-30"
                  >
                    🗑
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {adding ? (
        <div className="bg-paper border-2 border-ink p-4 rounded-btn">
          <DraftForm
            draft={draft}
            setDraft={setDraft}
            onSave={saveAdd}
            onCancel={() => setAdding(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={startAdd}
          className="w-full py-3 bg-paper border-2 border-ink font-display rounded-btn shadow-stamp-sm hover:shadow-stamp transition-shadow"
        >
          {t('account.addresses.add')}
        </button>
      )}
    </div>
  );
}

function DraftForm({
  draft,
  setDraft,
  onSave,
  onCancel,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const valid = Boolean(draft.label.trim()) && Boolean(draft.place_id);

  return (
    <div className="space-y-2">
      <Field label={t('account.addresses.editor.label')}>
        <input
          value={draft.label}
          onChange={(e) => set({ label: e.target.value })}
          className="w-full px-3 py-2 bg-paper border-2 border-ink rounded-btn-sm"
        />
      </Field>

      <PlacesAutocompleteInput
        initialValue={placesInitialValue(draft)}
        onPick={(parts) =>
          set({
            place_id: parts.place_id,
            lat: parts.lat,
            lng: parts.lng,
            formatted_address: parts.formatted_address,
            street: parts.street,
            city: parts.city,
            zip: parts.zip,
          })
        }
      />

      {draft.formatted_address && (
        <p className="text-sm text-ink/80" data-testid="picked-address">
          📍 {draft.formatted_address}
        </p>
      )}
      {!draft.place_id && (
        <p className="text-xs text-ink/60">
          {t('account.addresses.editor.pickFirst')}
        </p>
      )}

      <Field label={t('account.addresses.editor.instructions')}>
        <input
          value={draft.instructions}
          onChange={(e) => set({ instructions: e.target.value })}
          className="w-full px-3 py-2 bg-paper border-2 border-ink rounded-btn-sm"
        />
      </Field>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!valid}
          className="flex-1 py-2 bg-ink text-paper font-display rounded-btn-sm disabled:opacity-40"
        >
          {t('account.addresses.editor.save')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 bg-paper border-2 border-ink font-display rounded-btn-sm"
        >
          {t('account.addresses.editor.cancel')}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-display text-ink mb-1">{label}</span>
      {children}
    </label>
  );
}
